import React, { FC, useEffect, useMemo, useState } from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import { textToColor } from 'helpers/textToColor/textToColor';
import { MAX_VISIBLE_OPTIONS, normalize } from 'helpers/utils/variantOrdering';
import { shouldUseDropdown, sortAxisValues } from '../helpers/optionAxes';

type VariantSelectorProps = {
  attribute: string;
  options: string[];
  selectedOption?: string;
  isVisual: boolean;
  onSelect: (value: string) => void;
  enabled?: boolean;
  unavailableOptions?: string[];
  getOptionPrice?: (option: string) => string | undefined;
  optionLabels?: Record<string, string>;
  /**
   * The variant page an option leads to. When given, options render as links so crawlers can
   * reach every variant URL; a click still selects in place (the href is for the crawler, the
   * handler for the shopper), so nothing changes for the person using it.
   */
  getOptionHref?: (option: string) => string | undefined;
  /**
   * The full name of the variant an option leads to ("Express Silver - Level End Post Package - 36\""),
   * used as the link's accessible name: a bare "Silver" or "36 in" means little as anchor text.
   */
  getOptionLinkLabel?: (option: string) => string | undefined;
};

/** A primary-button click with no modifier: the only click a variant link turns into an in-place selection. */
export const isPlainLeftClick = (event: React.MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

const VariantSelector: FC<VariantSelectorProps> = ({
  attribute,
  options,
  selectedOption,
  isVisual,
  onSelect,
  enabled = true,
  unavailableOptions = [],
  getOptionPrice,
  optionLabels,
  getOptionHref,
  getOptionLinkLabel,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [inlineSelection, setInlineSelection] = useState<string | undefined>(selectedOption);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);

  useEffect(() => {
    setInlineSelection(selectedOption);
  }, [selectedOption]);

  const orderedOptions = useMemo(() => sortAxisValues(attribute, options), [attribute, options]);

  // Long dimension-like lists (lengths, heights) collapse to a dropdown. Swatches never do —
  // they are chosen by eye, so they keep the "see all" expander below.
  const useDropdown = !isVisual && shouldUseDropdown(attribute, orderedOptions.length);

  const hasMoreThanFive = orderedOptions.length > MAX_VISIBLE_OPTIONS;

  // ALWAYS derive from orderedOptions
  const visibleOptions = useMemo(() => {
    if (!isVisual) return orderedOptions;

    if (!hasMoreThanFive) return orderedOptions;

    return expanded ? orderedOptions : orderedOptions.slice(0, MAX_VISIBLE_OPTIONS);
  }, [orderedOptions, isVisual, expanded, hasMoreThanFive]);

  if (!enabled) return null;

  const isUnavailable = (option: string) => unavailableOptions.some((u) => normalize(u) === normalize(option));

  const handleSelect = (value: string) => {
    if (isUnavailable(value)) return;

    setInlineSelection(value);
    onSelect(value);
  };

  // Links keep the variant graph crawlable; a plain click is intercepted so selection stays in place,
  // while Cmd/Ctrl/Shift-click still opens the variant page as a link should. Space selects too,
  // like the buttons these replaced. Selection state is aria-current on a link (aria-pressed is
  // only valid on buttons) and aria-pressed on a button.
  const linkProps = (option: string, disabled: boolean, selected: boolean) => {
    const href = disabled ? undefined : getOptionHref?.(option);
    return href
      ? ({
          as: 'a' as const,
          href,
          'aria-current': selected ? ('true' as const) : undefined,
          onClick: (event: React.MouseEvent) => {
            if (!isPlainLeftClick(event)) return;
            event.preventDefault();
            handleSelect(option);
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key !== ' ') return;
            event.preventDefault();
            handleSelect(option);
          },
        } as const)
      : ({
          as: 'button' as const,
          type: 'button' as const,
          'aria-pressed': selected,
          disabled,
          onClick: () => handleSelect(option),
        } as const);
  };

  return (
    <div className="mt-12">
      {/* =========================
          SELECTED / HOVER DISPLAY
          ========================= */}
      {isVisual && (hoveredOption || inlineSelection) && (
        <Typography className="mb-8 text-sm font-medium text-black" aria-live="polite">
          {(() => {
            const option = hoveredOption || inlineSelection;
            const label = option ? (optionLabels?.[option] ?? textToColor(option).label) : '';
            const price = option ? getOptionPrice?.(option) : undefined;
            return `${hoveredOption ? 'Color: ' : 'Selected: '}${label}${price ? ` ${price}` : ''}`;
          })()}
        </Typography>
      )}

      {/* =========================
          DROPDOWN (long dimension lists)
          ========================= */}
      {useDropdown ? (
        <select
          aria-label={attribute}
          value={inlineSelection ?? ''}
          onChange={(event) => handleSelect(event.target.value)}
          className="w-full max-w-320 rounded-none border border-[#d7d7d7] p-12 text-14 text-[#212121] focus:border-[#212121] focus:outline-none"
        >
          {orderedOptions.map((option) => (
            <option key={`${attribute}-${option}`} value={option} disabled={isUnavailable(option)}>
              {optionLabels?.[option] ?? option}
            </option>
          ))}
        </select>
      ) : (
      <>
      {/* =========================
          OPTION GRID
          ========================= */}
      <div role="group" aria-label={attribute} className="flex flex-wrap gap-8">
        {visibleOptions.map((option) => {
          const selected = normalize(option) === normalize(inlineSelection);

          const disabled = isUnavailable(option);

          if (isVisual) {
            const color = textToColor(option);
            const { as: Visual, ...visualProps } = linkProps(option, disabled, selected);
            const fullName = Visual === 'a' ? getOptionLinkLabel?.(option) : undefined;

            return (
              <Visual
                key={`${attribute}-${option}`}
                {...visualProps}
                aria-disabled={disabled}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                className={`relative block size-32 transition ${selected ? 'ring-2 ring-[#212121] ring-offset-2' : 'border border-[#d7d7d7] hover:border-[#212121]'
                  } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                style={{
                  backgroundColor: color.code,
                }}
              >
                {/* Colour first (what the swatch shows), then the variant's full name for crawlers and screen readers. */}
                <span className="sr-only">
                  {color.label}
                  {fullName && ` – ${fullName}`}
                </span>
              </Visual>
            );
          }

          const { as: Option, ...optionProps } = linkProps(option, disabled, selected);
          const fullName = Option === 'a' ? getOptionLinkLabel?.(option) : undefined;
          return (
            <Option
              key={`${attribute}-${option}`}
              {...optionProps}
              aria-disabled={disabled}
              className={`inline-block border px-16 py-12 text-14 transition ${selected ? 'border-[#212121] bg-[#212121] text-white' : 'border-[#d7d7d7] bg-white hover:border-[#212121]'
                } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
            >
              {optionLabels?.[option] ?? option}
              {/* The visible label leads the link's name (WCAG 2.5.3); the full variant name follows for crawlers. */}
              {fullName && <span className="sr-only"> – {fullName}</span>}
            </Option>
          );
        })}
      </div>

      {/* =========================
          SEE ALL
          ========================= */}
      {isVisual && hasMoreThanFive && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-8 text-14 font-medium text-[#212121] underline focus:outline-none"
          aria-expanded={expanded}
        >
          {expanded ? 'See Less' : `See all ${orderedOptions.length} Options`}
        </button>
      )}
      </>
      )}
    </div>
  );
};

export default VariantSelector;
